import { useGetOne } from "ra-core";
import {
  ArrowLeft,
  CheckCircle2,
  Clapperboard,
  ExternalLink,
  MousePointerClick,
  RefreshCw,
  UserPlus,
} from "lucide-react";
import { Link, useParams } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

import type { TrackingLinkSummary } from "../types";
import {
  conversionPercentage,
  progressPercentage,
  trackingLinkFunnel,
  type TrackingLinkFunnelStage,
} from "./trackingLinkAnalytics";
import {
  trackingLinkPreviewUrl,
  trackingLinkUrl,
} from "./trackingLinkContract";

export function TrackingLinkAnalyticsPage() {
  const { id } = useParams();
  const {
    data: link,
    error,
    isPending,
    isFetching,
    refetch,
  } = useGetOne<TrackingLinkSummary>(
    "tracking_links_summary",
    { id: id ?? "" },
    { enabled: Boolean(id) },
  );

  if (isPending) {
    return (
      <p className="p-6 text-sm text-muted-foreground">Loading analytics…</p>
    );
  }

  if (error || !link) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <BackButton />
        <Card>
          <CardContent className="py-8 text-sm text-destructive">
            Could not load this tracking link:{" "}
            {String(error ?? "Link not found")}
          </CardContent>
        </Card>
      </div>
    );
  }

  const funnel = trackingLinkFunnel(link);
  const redirects = funnel[0].value;
  const contacts = funnel[1].value;
  const started = funnel[2].value;
  const completed = funnel[3].value;

  return (
    <div className="p-4 md:p-6 space-y-5 pb-20">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <BackButton />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold">Link analytics</h1>
              <StatusBadge status={link.status} />
            </div>
            <p className="mt-1 text-sm font-medium">{link.name}</p>
            <a
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              href={trackingLinkPreviewUrl(link.slug)}
              target="_blank"
              rel="noreferrer"
              aria-label={`Preview ${link.name}; preview redirects are excluded from counted clicks`}
            >
              Safe preview
              <ExternalLink className="size-3.5" aria-hidden="true" />
            </a>
            <p className="text-xs text-muted-foreground">
              {trackingLinkUrl(link.slug)} · Copy the link from the list to
              share the countable version.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={`size-4 ${isFetching ? "animate-spin" : ""}`}
            aria-hidden="true"
          />
          Refresh
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Counted person clicks"
          value={redirects}
          icon={MousePointerClick}
          detail={`${formatNumber(link.preview_clicks ?? 0)} previews excluded`}
        />
        <MetricCard label="New contacts" value={contacts} icon={UserPlus} />
        <MetricCard
          label="Echoes started"
          value={started}
          icon={Clapperboard}
          detail="Includes returning users"
        />
        <MetricCard
          label="Echoes completed"
          value={completed}
          icon={CheckCircle2}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <Card>
          <CardHeader>
            <CardTitle>Funnel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {funnel.map((stage) => (
              <FunnelRow key={stage.key} stage={stage} />
            ))}
            <div className="space-y-1 border-t pt-4 text-xs text-muted-foreground">
              <p>
                Counted clicks are redirect events, not unique people. CRM and
                automated previews are recorded separately and excluded.
              </p>
              <p>
                New contacts is first-touch only. Echo stages can include
                returning users whose next eligible Echo credited this link.
              </p>
              <p>
                Clicks update immediately. Contact and Echo totals include
                internal/test users and normally refresh within 30 minutes.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Conversion rates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <RateRow
                label="Click → new contact"
                value={contacts}
                total={redirects}
              />
              <RateRow
                label="Click → Echo started"
                value={started}
                total={redirects}
              />
              <RateRow
                label="Click → Echo completed"
                value={completed}
                total={redirects}
              />
              <RateRow
                label="Started → completed"
                value={completed}
                total={started}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Attribution details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <DetailRow label="Felix code" value={link.public_code} mono />
              <DetailRow
                label="All recorded redirects"
                value={formatNumber(link.total_clicks ?? 0)}
              />
              <DetailRow
                label="Excluded previews"
                value={formatNumber(link.preview_clicks ?? 0)}
              />
              <DetailRow label="UTM source" value={link.utm_source} />
              <DetailRow label="UTM medium" value={link.utm_medium} />
              <DetailRow label="UTM campaign" value={link.utm_campaign} />
              <DetailRow label="UTM content" value={link.utm_content} />
              <DetailRow label="Created" value={formatDate(link.created_at)} />
              <DetailRow
                label="Last redirect"
                value={formatDate(link.last_clicked_at)}
              />
              <DetailRow
                label="Totals calculated"
                value={formatDate(link.metrics_computed_at)}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

TrackingLinkAnalyticsPage.path = "/tracking-links/:id/analytics";

function BackButton() {
  return (
    <Button variant="ghost" size="sm" asChild className="-ml-2">
      <Link to="/tracking-links">
        <ArrowLeft className="size-4" aria-hidden="true" />
        Back to tracking links
      </Link>
    </Button>
  );
}

function MetricCard({
  label,
  value,
  icon: Icon,
  detail,
}: {
  label: string;
  value: number;
  icon: typeof MousePointerClick;
  detail?: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-3 p-5">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {formatNumber(value)}
          </p>
          {detail ? (
            <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
          ) : null}
        </div>
        <Icon className="size-5 text-muted-foreground" aria-hidden="true" />
      </CardContent>
    </Card>
  );
}

function FunnelRow({ stage }: { stage: TrackingLinkFunnelStage }) {
  const rate = conversionPercentage(stage.value, stage.total);
  return (
    <div className="grid grid-cols-[8.5rem_minmax(0,1fr)_6.5rem] items-center gap-3">
      <span className="text-sm">{stage.label}</span>
      <Progress value={progressPercentage(stage.value, stage.total)} />
      <span className="text-right text-sm tabular-nums">
        {formatNumber(stage.value)}{" "}
        {stage.total > 0 ? `(${formatPercentage(rate)})` : ""}
      </span>
    </div>
  );
}

function RateRow({
  label,
  value,
  total,
}: {
  label: string;
  value: number;
  total: number;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">
        {total > 0 ? formatPercentage(conversionPercentage(value, total)) : "—"}
      </span>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={`max-w-[12rem] text-right break-words ${mono ? "font-mono" : ""}`}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function StatusBadge({ status }: Pick<TrackingLinkSummary, "status">) {
  return (
    <Badge variant={status === "active" ? "default" : "secondary"}>
      {status}
    </Badge>
  );
}

function formatNumber(value: number) {
  return new Intl.NumberFormat().format(value);
}

function formatPercentage(value: number) {
  return `${Math.round(value)}%`;
}

function formatDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
